/**
 * The kiosk's session controller: the one place where server events change state and where the
 * user's intents leave for the backend. Pure TypeScript — React only reads its stores.
 *
 * Three roles, one controller:
 *  - `kiosk`    the totem: starts the session, sends what the user does;
 *  - `operator` a remote helper on the same session: sees what the user sees, can touch and write for them;
 *  - `replay`   a recorded session played back: same screens, read-only.
 */
import { report, describeError } from './diagnostics';
import {
  emptyConversation, noticed, replied, sendFailed, timedOut, turnFailed, turnOpened, typed, userSaid, withdrawn,
  type Conversation,
} from './conversation';
import type { BackendApi, Delivery, EventFeed, FeedStatus, RuntimeInfo, SpokenChannel } from './ports';
import { createStore, type Store } from './store';
import type { AccessibilityMode, ServerEvent, Session } from './wire';

export type KioskRole = 'kiosk' | 'operator' | 'replay';

/** What became of words sent to be said: queued on the open voice connections, or why not. */
export type SpeakOutcome = { ok: true; queued: number; delivery: Delivery } | { ok: false; reason: string };

/** How long words with no deadline wait for the kiosk's voice to open (the backend always sends one). */
const WAKE_WAIT_MS = 30_000;

export type ConnectionStatus = 'starting' | 'connecting' | 'online' | 'retrying' | 'offline' | 'replay';

export interface Connection {
  status: ConnectionStatus;
  detail?: string;
}

export interface InactivityWarning {
  message: string;
  /** Epoch ms when the backend will reset the session. */
  deadline: number;
}

export interface KioskStores {
  session: Store<Session | null>;
  conversation: Store<Conversation>;
  connection: Store<Connection>;
  runtime: Store<RuntimeInfo | null>;
  inactivity: Store<InactivityWarning | null>;
  /** The accessibility mode the backend announced; the session's own field wins when it is set. */
  announcedMode: Store<AccessibilityMode | null>;
  /** Until when (ms) words wait on the backend for this kiosk to open its closed voice and say them; the kiosk role only. */
  voiceWanted: Store<number | null>;
}

export interface KioskOptions {
  sessionId: string;
  role: KioskRole;
  api: BackendApi;
  /** Live server events; none for a replay, which pushes them with `apply`. */
  feed: EventFeed | null;
  turnTimeoutMs?: number;
  heartbeatEveryMs?: number;
  now?: () => number;
}

export interface Kiosk {
  readonly sessionId: string;
  readonly role: KioskRole;
  readonly stores: KioskStores;
  start(): Promise<void>;
  stop(): void;
  /** Feed one server event in (the live feed and the replay both come through here). */
  apply(event: ServerEvent): void;
  press(button: string, data?: Record<string, unknown>): Promise<void>;
  say(text: string): Promise<void>;
  setLanguage(language: string): Promise<void>;
  restart(): Promise<void>;
  /** Any touch on the screen: keeps the session alive (throttled). */
  touched(): void;
  /** "I'm still here" on the inactivity warning. */
  stillHere(): void;
  scanQr(data: string): Promise<void>;
  /** Words said aloud to the person at the kiosk by someone helping from afar (the operator's console). */
  speakToUser(text: string, options?: { chime?: boolean; wake?: boolean }): Promise<SpeakOutcome>;
  useSpokenChannel(channel: SpokenChannel | null): void;
  /** Start again from an empty state (the replay seeks by replaying from the start). */
  clear(): void;
}

export function createKiosk(options: KioskOptions): Kiosk {
  const { sessionId, role, api, feed } = options;
  const turnTimeoutMs = options.turnTimeoutMs ?? 15_000;
  const heartbeatEveryMs = options.heartbeatEveryMs ?? 15_000;
  const now = options.now ?? Date.now;

  const stores: KioskStores = {
    session: createStore<Session | null>('session', null),
    conversation: createStore<Conversation>('conversation', emptyConversation),
    connection: createStore<Connection>('connection', { status: role === 'replay' ? 'replay' : 'starting' }),
    runtime: createStore<RuntimeInfo | null>('runtime', null),
    inactivity: createStore<InactivityWarning | null>('inactivity', null),
    announcedMode: createStore<AccessibilityMode | null>('announcedMode', null),
    voiceWanted: createStore<number | null>('voiceWanted', null),
  };

  let closeFeed: (() => void) | null = null;
  let ticker: ReturnType<typeof setInterval> | null = null;
  let spoken: SpokenChannel | null = null;
  let lastHeartbeat = 0;
  let wasRetrying = false;

  const conversation = (f: (c: Conversation) => Conversation) => stores.conversation.set(f);

  function apply(event: ServerEvent): void {
    switch (event.kind) {
      case 'session':
        if (event.session.session_id !== sessionId && role !== 'replay') {
          report('warn', 'kiosk', `ignored a session update for ${event.session.session_id}`);
          return;
        }
        stores.session.set(event.session);
        stores.inactivity.set(null);
        return;
      case 'user-said':
        conversation((c) => userSaid(c, event.turnId, event.text, event.source, now()));
        stores.inactivity.set(null);
        return;
      case 'processing':
        conversation((c) => turnOpened(c, event.turnId, now()));
        return;
      case 'understood':
        return;
      case 'reply':
        conversation((c) => replied(c, event.message, now()));
        return;
      case 'reset':
        spoken?.silence(); // whoever reset it (operator, inactivity), the old reply is not heard on
        stores.conversation.set(emptyConversation);
        stores.inactivity.set(null);
        stores.announcedMode.set(null);
        if (role !== 'replay') void refetch();
        return;
      case 'inactivity-warning':
        stores.inactivity.set({ message: event.message, deadline: now() + event.countdownS * 1000 });
        return;
      case 'turn-error':
        conversation((c) => turnFailed(c, event.turnId, event.message, now()));
        report('warn', 'backend', `turn ${event.turnId} failed: ${event.message}`);
        return;
      case 'vision-error':
        conversation((c) => noticed(turnFailed(c, event.turnId, '', now()), { key: 'chat.vision_failed', tone: 'warn' }, now()));
        return;
      case 'accessibility-changed':
        stores.announcedMode.set(event.mode);
        return;
      case 'withdrawn':
        conversation((c) => withdrawn(c, event.text));
        return;
      case 'announcement':
        conversation((c) => noticed(c, {
          text: event.text,
          label: event.source === 'operator' ? 'chat.from_operator' : 'chat.announcement',
          tone: event.priority === 'critical' ? 'warn' : 'info',
        }, now()));
        if (event.wake && role === 'kiosk') stores.voiceWanted.set(now() + (event.ttlS !== null ? event.ttlS * 1000 : WAKE_WAIT_MS));
        return;
    }
  }

  /** The backend's session as it is; one it does not know (new, or collected) the kiosk starts afresh. */
  async function join(): Promise<void> {
    const s = await api.getSession(sessionId);
    if (s) stores.session.set(s);
    else if (role === 'kiosk') stores.session.set(await api.resetSession(sessionId));
    else report('warn', 'kiosk', `session ${sessionId} not found on the backend`);
  }

  async function refetch(): Promise<void> {
    try {
      await join();
    } catch (e) {
      report('warn', 'transport', `could not read the session: ${describeError(e)}`);
    }
  }

  function onFeedStatus(status: FeedStatus, detail?: string): void {
    if (status === 'online') {
      stores.connection.set({ status: 'online' });
      if (wasRetrying) void refetch(); // events may have been lost while away: the snapshot is the truth
      wasRetrying = false;
    } else {
      if (status === 'retrying') wasRetrying = true;
      stores.connection.set(detail ? { status, detail } : { status });
    }
  }

  function readOnly(what: string): boolean {
    if (role !== 'replay') return false;
    report('info', 'replay', `${what}: a replay is read-only`);
    return true;
  }

  async function sendTurn(send: () => Promise<{ turnId: string }>, failedEntry: string | null): Promise<void> {
    try {
      const { turnId } = await send();
      conversation((c) => turnOpened(c, turnId, now()));
    } catch (e) {
      conversation((c) => sendFailed(c, failedEntry, 'chat.send_failed', now()));
      report('error', 'transport', `could not send: ${describeError(e)}`);
    }
  }

  return {
    sessionId,
    role,
    stores,
    apply,

    async start() {
      api.runtime().then((r) => stores.runtime.set(r)).catch((e) => report('warn', 'transport', `no runtime info: ${describeError(e)}`));
      if (role === 'replay') return;
      stores.connection.set({ status: 'connecting' });
      if (feed) closeFeed = feed.open(sessionId, { onEvent: apply, onStatus: onFeedStatus });
      try {
        await join(); // a reload rejoins the session where it was; the backend's inactivity reset ends it
      } catch (e) {
        stores.connection.set({ status: 'offline', detail: describeError(e) });
        report('error', 'transport', `backend not reachable: ${describeError(e)}`);
      }
      ticker = setInterval(() => conversation((c) => timedOut(c, now(), turnTimeoutMs)), 1000);
    },

    stop() {
      closeFeed?.();
      closeFeed = null;
      if (ticker) clearInterval(ticker);
      ticker = null;
    },

    async press(button, data = {}) {
      if (readOnly(`press ${button}`)) return;
      lastHeartbeat = now();
      if (spoken?.press(button, data)) return; // the answer comes back aloud; the turn opens with the SSE ack
      await sendTurn(() => api.press(sessionId, button, data), null);
    },

    async say(text) {
      const words = text.trim();
      if (!words || readOnly('say')) return;
      lastHeartbeat = now();
      const { conversation: c, entryId } = typed(stores.conversation.get(), words, now());
      stores.conversation.set(c);
      if (spoken?.say(words)) return;
      await sendTurn(() => api.say(sessionId, words), entryId);
    },

    async setLanguage(language) {
      if (readOnly('language')) return;
      try {
        stores.session.set(await api.setLanguage(sessionId, language));
      } catch (e) {
        report('error', 'transport', `could not set the language: ${describeError(e)}`);
      }
    },

    async speakToUser(text, options = {}) {
      const words = text.trim();
      if (!words) return { ok: false, reason: 'nothing to say' };
      if (readOnly('speak to the user')) return { ok: false, reason: 'a replay sends nothing' };
      try {
        const { queued, delivery } = await api.announce(sessionId, words, {
          source: 'operator', chime: options.chime ?? false, ...(options.wake !== undefined ? { wake: options.wake } : {}),
        });
        return { ok: true, queued, delivery };
      } catch (e) {
        report('warn', 'transport', `could not speak to the user: ${describeError(e)}`);
        return { ok: false, reason: describeError(e) };
      }
    },

    async restart() {
      if (readOnly('restart')) return;
      spoken?.silence(); // at the press, not when the backend's reset comes back
      try {
        stores.conversation.set(emptyConversation);
        stores.session.set(await api.resetSession(sessionId));
      } catch (e) {
        report('error', 'transport', `could not restart: ${describeError(e)}`);
      }
    },

    touched() {
      if (role === 'replay' || now() - lastHeartbeat < heartbeatEveryMs) return;
      lastHeartbeat = now();
      api.heartbeat(sessionId).catch((e) => report('warn', 'transport', `heartbeat failed: ${describeError(e)}`));
    },

    stillHere() {
      stores.inactivity.set(null);
      if (role === 'replay') return;
      lastHeartbeat = now();
      api.heartbeat(sessionId).catch((e) => report('warn', 'transport', `heartbeat failed: ${describeError(e)}`));
    },

    async scanQr(data) {
      if (readOnly('scan')) return;
      await sendTurn(() => api.scanQr(sessionId, data), null);
    },

    useSpokenChannel(channel) {
      spoken = channel;
    },

    clear() {
      stores.session.set(null);
      stores.conversation.set(emptyConversation);
      stores.inactivity.set(null);
      stores.announcedMode.set(null);
      stores.voiceWanted.set(null);
    },
  };
}
